import React, { useState, useCallback, useMemo, MouseEvent } from 'react';
import { TextField, FormControlLabel, Button, Checkbox, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
	GridPanelContent,
	GridPanelFooter,
	GridPanelHeader,
	GridPanelWrapper,
	useGridApiContext,
	useGridSelector,
	allGridColumnsSelector,
	GridColDef,
} from '@mui/x-data-grid-pro';
import { RiCheckboxBlankLine, RiCheckboxFill } from 'react-icons/ri';

type DataGridName = 'inventory_articles' | 'inventory_sku' | 'default';

const isIncludedInSearch = (searchValue: string) => (column: GridColDef) =>
	column.field.toLowerCase().indexOf(searchValue.toLowerCase()) > -1 ||
	(column.headerName && column.headerName.toLowerCase().indexOf(searchValue.toLowerCase()) > -1);

interface Props {
	dataGridName: DataGridName;
}

const CustomColumnsPanel = (props: Props) => {
	const classes = useStyles();
	const theme = useTheme();

	const apiRef = useGridApiContext();
	const columns: GridColDef[] = useGridSelector(apiRef, allGridColumnsSelector);
	const [searchValue, setSearchValue] = useState('');

	const toggleColumn = useCallback(
		(event: MouseEvent<HTMLButtonElement>) => {
			if (!apiRef) {
				return;
			}
			const { name } = event.target as HTMLInputElement;
			const column = apiRef.current.getColumn(name);
			apiRef.current.setColumnVisibility(name, !!column.hide);
		},
		[apiRef],
	);

	const toggleAllColumns = useCallback(
		(value: boolean) => {
			if (!apiRef) {
				return;
			}
			apiRef.current.updateColumns(
				columns.map((col) => {
					col.hide = value;
					return col;
				}),
			);
		},
		[apiRef, columns],
	);
	const showAllColumns = useCallback(() => toggleAllColumns(false), [toggleAllColumns]);
	const hideAllColumns = useCallback(() => toggleAllColumns(true), [toggleAllColumns]);

	const handleSearchValueChange = useCallback((event) => {
		setSearchValue(event.target.value);
	}, []);

	const currentColumns: GridColDef[] = useMemo(
		() => (!searchValue ? columns : columns.filter(isIncludedInSearch(searchValue))),
		[columns, searchValue],
	);

	return (
		<GridPanelWrapper>
			<GridPanelHeader>
				<TextField
					autoFocus
					label={apiRef!.current.getLocaleText('columnsPanelTextFieldLabel')}
					placeholder={apiRef!.current.getLocaleText('columnsPanelTextFieldPlaceholder')}
					value={searchValue}
					onChange={handleSearchValueChange}
					variant="standard"
					fullWidth
				/>
			</GridPanelHeader>
			<GridPanelContent>
				<div className={classes.container}>
					{currentColumns.map((column) => (
						<div key={column.field} className={classes.column}>
							<FormControlLabel
								control={
									<Checkbox
										name={column.field}
										checked={!column.hide}
										size="small"
										onClick={toggleColumn}
										icon={
											<RiCheckboxBlankLine color={theme.palette.colors.tundra.main} size={20} />
										}
										checkedIcon={<RiCheckboxFill color={theme.palette.primary.main} size={20} />}
									/>
								}
								label={column.headerName ?? ''}
							/>
						</div>
					))}
				</div>
			</GridPanelContent>
			<GridPanelFooter>
				<Button onClick={hideAllColumns} color="primary">
					{apiRef!.current.getLocaleText('columnsPanelHideAllButton')}
				</Button>
				<Button onClick={showAllColumns} color="primary">
					{apiRef!.current.getLocaleText('columnsPanelShowAllButton')}
				</Button>
			</GridPanelFooter>
		</GridPanelWrapper>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		container: {
			padding: '8px 0px 8px 8px',
		},
		column: {
			display: 'flex',
			justifyContent: 'space-between',
			padding: '1px 8px 1px 7px',
		},
		switch: {
			marginRight: 4,
		},
		dragIcon: {
			justifyContent: 'flex-end',
		},
	}),
);

export default CustomColumnsPanel;
